import { FileText, ClipboardList, Handshake, UserRound, Wrench, CalendarClock, ReceiptText, Receipt, MailCheck } from "lucide-react";
import { cn } from "@/lib/utils";

/** «Sendt» – rapporten er sendt til kunde på e-post (frå Outlook). Viser ingenting når count er 0. */
export const EmailSentBadge = ({ count = 0, className, compact }) => {
  if (!count) return null;
  return (
    <span
      data-testid="email-sent-badge"
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        "border-[rgba(31,138,76,0.35)] bg-[rgba(31,138,76,0.10)] text-[#1F8A4C] dark:text-[#5BD08B]",
        className
      )}
      title={count > 1 ? `Sendt ${count} gonger` : "Sendt til kunde"}
    >
      <MailCheck className="h-3.5 w-3.5" />
      {compact ? "Sendt" : count > 1 ? `Sendt ×${count}` : "Sendt"}
    </span>
  );
};

/** Faktura-status på rapport: fakturert (grøn) / ikkje fakturert (nøytral). */
export const InvoiceBadge = ({ invoiced, className, compact }) => (
  <span
    data-testid={`invoice-badge-${invoiced ? "invoiced" : "open"}`}
    className={cn(
      "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
      invoiced
        ? "border-[rgba(31,138,76,0.35)] bg-[rgba(31,138,76,0.10)] text-[#1F8A4C] dark:text-[#5BD08B]"
        : "border-dashed border-border bg-transparent text-muted-foreground",
      className
    )}
  >
    {invoiced ? <ReceiptText className="h-3.5 w-3.5" /> : <Receipt className="h-3.5 w-3.5" />}
    {compact ? (invoiced ? "Fakturert" : "Ikkje fakt.") : invoiced ? "Fakturert" : "Ikkje fakturert"}
  </span>
);

export const TypeBadge = ({ type, className, size = "sm" }) => {
  const agreement = type === "serviceavtale";
  const Icon = agreement ? Handshake : UserRound;
  return (
    <span
      data-testid={`customer-type-badge-${type || "engangskunde"}`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border font-semibold whitespace-nowrap",
        size === "sm" ? "px-2.5 py-0.5 text-xs" : "px-3 py-1 text-sm",
        agreement
          ? "border-[#0B0D10]/80 bg-[#0B0D10] text-white dark:border-white/30 dark:bg-white dark:text-[#0B0D10]"
          : "border-border bg-muted text-muted-foreground",
        className
      )}
    >
      <Icon className={size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
      {agreement ? "Serviceavtale" : "Engangskunde"}
    </span>
  );
};

export const ReportTypeBadge = ({ type, className }) => {
  const jobb = type === "jobb";
  const Icon = jobb ? ClipboardList : FileText;
  return (
    <span
      data-testid={`report-type-badge-${type || "service"}`}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap",
        jobb
          ? "border-primary/30 bg-primary/10 text-[#9a2f00] dark:text-primary"
          : "border-border bg-card text-foreground",
        className
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {jobb ? "Jobbrapport" : "Servicerapport"}
    </span>
  );
};

/** Icon tile for deadline kind (service vs årskontroll) */
export const KindIcon = ({ kind, className }) => {
  const annual = kind === "annual";
  const Icon = annual ? CalendarClock : Wrench;
  return (
    <span
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
        annual ? "bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]" : "bg-primary/12 text-[#9a2f00] dark:text-primary",
        className
      )}
      style={!annual ? { backgroundColor: "rgba(255,78,0,0.12)" } : undefined}
    >
      <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
    </span>
  );
};
